import { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Router } from './router';
import { useWebSocket } from './hooks/useWebSocket';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useAutoReleaseConfirmHandler } from './hooks/useAutoReleaseConfirmHandler';
import { InstallPWAPrompt } from './components/ui/InstallPWAPrompt';
import { IOSPWAGuide } from './components/IOSPWAGuide';
import { useIOSPWAInstall } from './hooks/useIOSPWAInstall';
import { UpdateNotification } from './components/UpdateNotification';
import { SplashScreen } from './components/SplashScreen';
import ConfirmAutoReleaseDialog from './components/ConfirmAutoReleaseDialog';
import { LevelUpModal } from './components/LevelUpModal';
import analytics from './lib/analytics';
import { useAuthStore } from './store/authStore';
import { TranslationProvider } from './i18n';
import { reconcileAutoReleasePermission, reconcilePushPermission } from './lib/permissionReconciliation';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Inner component so useWebSocket can access QueryClientProvider
function AppShell() {
  useWebSocket();
  usePushNotifications();
  const { showIOSGuide, dismissGuide } = useIOSPWAInstall();
  const user = useAuthStore((s) => s.user);

  // v1.1 — Smart Auto-Release notify-mode dialog state. Opens when the
  // user taps a `auto_release_confirm` push notification (native only).
  const [pendingConfirmId, setPendingConfirmId] = useState<string | null>(null);
  const openConfirmDialog = useCallback((id: string) => setPendingConfirmId(id), []);
  useAutoReleaseConfirmHandler(openConfirmDialog);

  // P3.3: re-apply GA4 user properties whenever the persisted user changes
  // (covers app boot from persisted state, where login/register handlers
  // never run). Set once per user.id change.
  useEffect(() => {
    if (!user) return;
    analytics.setUserId(user.id);
    analytics.setUserProperties({
      buildingId: user.building.id,
      userRole: user.super_admin ? 'super_admin' : user.is_admin ? 'admin' : 'resident',
    });
  }, [user?.id]);

  // Self-heal stale "enabled" flags on every app launch (once per user
  // session) — see lib/permissionReconciliation.ts. Catches the case where
  // the OS permission was revoked, or a fresh install started with no OS
  // grant at all while the server still remembers a prior enable.
  useEffect(() => {
    if (!user) return;
    void reconcileAutoReleasePermission();
    void reconcilePushPermission();
  }, [user?.id]);

  // Track offline usage
  useEffect(() => {
    const handleOnline = () => {
      if (!navigator.onLine) return;
      analytics.trackOfflineToOnlineSync();
    };

    const handleOffline = () => {
      analytics.trackOfflineUsage();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check if already offline
    if (!navigator.onLine) {
      analytics.trackOfflineUsage();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      <UpdateNotification />
      <Router />
      <InstallPWAPrompt />
      <IOSPWAGuide isVisible={showIOSGuide} onDismiss={dismissGuide} />
      {user && (
        <ConfirmAutoReleaseDialog
          open={!!pendingConfirmId}
          releaseId={pendingConfirmId}
          onClose={() => setPendingConfirmId(null)}
        />
      )}
      {user && <LevelUpModal />}
    </>
  );
}

function App() {
  const bootstrapping = useAuthStore((s) => s.bootstrapping);

  useEffect(() => {
    analytics.init();

    // Set app version in analytics and Sentry (will be initialized from API response)
    const appVersion = sessionStorage.getItem('app_version');
    if (appVersion) {
      analytics.setAppVersion(appVersion);
      Sentry.setTag('app_version', appVersion);
    }

    // Cold-start bootstrap: if a persisted refresh token exists, silently
    // mint a new access token BEFORE the router decides where to send the
    // user. Without this, ProtectedRoute briefly sees `accessToken === null`
    // (it's never persisted) and bounces to /login → "login flicker".
    useAuthStore.getState().bootstrap();
  }, []);

  if (bootstrapping) {
    return <SplashScreen />;
  }

  return (
    <TranslationProvider>
      <QueryClientProvider client={queryClient}>
        <AppShell />
      </QueryClientProvider>
    </TranslationProvider>
  );
}

export default Sentry.withProfiler(App);
